import { EssentialsCategoryModalWrapperProps } from '../../types/components/collections/EssentialsCategoryModalWrapper';
import { EssentialsCollectionsPagedResponse } from '../../types/api/CollectionsIndexTypes';
import { GenericResponse } from '../../types/api/Http';

import { ENV } from '../../constants/environments';

import { useState, useEffect } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import httpHelpers from '../../helpers/http';
import { addMessage } from '../../state/actions/messagesActions';

import { EssentialsCategoryModal } from './EssentialsCategoryModal';
import { Loader } from '../shared/Loader';

import { getEssentialsTotals } from '../../helpers/essentialsCatalogHelpers';

const EssentialsCategoryModalWrapper: React.FC<EssentialsCategoryModalWrapperProps> = ({
  isOpen,
  setIsOpen,
}) => {
  const { mapQueryParams } = httpHelpers;
  const dispatch = useDispatch();

  const [collectionsPagedView, setCollectionsPagedView] = useState<EssentialsCollectionsPagedResponse>();
  const [internalIsOpenValue, setInternalIsOpenValue] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(false);
  const [selectedLibraryId, setSelectedLibraryId] = useState<number>();
  // have to set these values separately b/c when API is called with an library_id filter,
  // the excluded library total count is returned as 0, thus hiding the according library filter button
  const [total2D, setTotal2D] = useState<number>();
  const [total3D, setTotal3D] = useState<number>();
  const [total, setTotal] = useState<number>();

  const loadCollections = async (config?: { page?: number, libraryId?: number }): Promise<EssentialsCollectionsPagedResponse> => {
    const queryParams: { [key: string]: string } = {};
    const page = config?.page || 1;
    const libraryId = config?.libraryId;
    queryParams.page = page.toString();
    if (libraryId)
      queryParams.library_id = libraryId.toString();
    const collectionUrl = mapQueryParams(`${ENV.api.baseURL}/products/essentials_catalog`, queryParams);
    try {
      const response: GenericResponse<EssentialsCollectionsPagedResponse> = await axios.get(collectionUrl);
      return response.data;
    } catch {
      dispatch(
        addMessage({
          type: 'error',
          body: 'Something went wrong loading Essentials catalog. Please try again later or contact support.'
        })
      );
    }
  }

  const loadFirstCollectionsAndOpenModal = async () => {
    setInitialLoading(true);
    const essentialTotals = await getEssentialsTotals()
    const collectionsPagedView = await loadCollections();
    setTotal2D(essentialTotals.total2D);
    setTotal3D(essentialTotals.total3D);
    // API returns wrong total amount, so this is a workaround
    setTotal(essentialTotals.fullTotal)
    setCollectionsPagedView(collectionsPagedView);
    setInternalIsOpenValue(true);
    setInitialLoading(false);
  }

  const loadNextPage = async () => {
    if (!collectionsPagedView) return;
    const { page: currentPage, total_pages } = collectionsPagedView;
    if (currentPage >= total_pages) return;
    const newPage = await loadCollections({ page: currentPage + 1, libraryId: selectedLibraryId });
    if (!newPage?.data) return;
    const currentCollections = collectionsPagedView.data;
    const newCollections = newPage.data;
    newPage.data = [ ...currentCollections, ...newCollections ];
    setCollectionsPagedView(newPage);
  }

  const setIsOpenCallback = (value: boolean) => {
    setInternalIsOpenValue(value);
    setIsOpen(value);
  }

  const allPagesLoaded = (): boolean => {
    if (!collectionsPagedView) return false;
    const { page: currentPage, total_pages } = collectionsPagedView;
    return currentPage === total_pages;
  }

  const onLibrarySelected = async (libraryId: number) => {
    if (libraryId === selectedLibraryId) return;
    setSelectedLibraryId(libraryId);
    const result = await loadCollections({ libraryId });
    setCollectionsPagedView(result);
  }

  useEffect(() => {
    if (isOpen !== true) return;
    !!collectionsPagedView ?
      setInternalIsOpenValue(true) :
      loadFirstCollectionsAndOpenModal();
  }, [isOpen]);

  return (
    <>
      {
        initialLoading &&
        <Loader />
      }
      {
        internalIsOpenValue &&
        <EssentialsCategoryModal
          collections={collectionsPagedView?.data}
          modalOpen={internalIsOpenValue}
          setModalOpen={setIsOpenCallback}
          total_count={total}
          total_2D={total2D}
          total_3D={total3D}
          loadNextPage={loadNextPage}
          allPagesLoaded={allPagesLoaded()}
          onLibrarySelected={onLibrarySelected}
        />
      }
    </>
  )
};

export default EssentialsCategoryModalWrapper;
